import { Injectable, inject } from '@angular/core';
import { translate } from '@jsverse/transloco';

import { Constants } from 'app/constants/constants';
import { AlertService } from 'app/services/alert.service';
import { EnableClassTelemetry } from 'app/services/otel.service';
import { ChartDisplayService } from '../../../services/chart-display.service';
import { PointSelectionRule } from './services/validation/widget-ai-insights-validator.service';

const INSIGHT_POINT_LABEL_HTML = '<span class="kxi-icon kxi-icon-lightbulb cx"></span>';

export interface ChartPointSelectionMatchmakerOptions {
  minPoints: number;
  maxPoints?: number;
  pointSelectionRule: PointSelectionRule;
}

@EnableClassTelemetry()
@Injectable()
export class ChartAiInsightPointSelectionService {
  private originalClickHandlerFn;
  private selectionCounter = 1;

  private chartDisplayService = inject(ChartDisplayService);
  private alertService = inject(AlertService);

  constructor() {}

  getDataPointCount(target: HTMLElement, pointSelectionRule: PointSelectionRule): number {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    switch (chart.options.chart.type) {
      case 'gauge':
        return chart.comparisonMetric ? 2 : 1;
      default:
        if (
          pointSelectionRule.rule === 'SAME_SERIES' ||
          pointSelectionRule.rule === 'ALL_SAME_SERIES'
        ) {
          if (chart.series.every((s) => s.data.length === 1)) {
            return 1;
          }
          return Math.max(...chart.series.map((s) => s.data.length)) * chart.series.length;
        }
        if (
          pointSelectionRule.rule === 'SAME_GROUP' ||
          pointSelectionRule.rule === 'ALL_SAME_GROUP'
        ) {
          return Math.max(...chart.series.map((s) => s.data.length));
        }
        return chart.series.map((s) => s.data.length).reduce((prev, curr) => prev + curr, 0);
    }
  }

  autoSelectPoints(target: HTMLElement, _options: ChartPointSelectionMatchmakerOptions) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    switch (chart.options.chart.type) {
      case 'gauge':
        const dataPoint = chart.series[0].data[0];
        const dataPoints = [
          {
            y: dataPoint.y,
            documentCount: dataPoint.documentCount,
            name: dataPoint.name,
            series: { index: 0 } as any,
          },
        ];
        if (chart.comparisonMetric) {
          dataPoints[0].series.name = dataPoint.series.options.custom.comparisonNames[0];

          const comparisonDataPoint = chart.series[1].data[0];
          dataPoints.push({
            y: comparisonDataPoint.y,
            documentCount: comparisonDataPoint.documentCount,
            name: comparisonDataPoint.name,
            series: {
              index: 1,
              name: comparisonDataPoint.series.options.custom.comparisonNames[1],
            },
          });
        }
        return dataPoints;
      default:
        return chart.series.flatMap((s) => s.data);
    }
  }

  allowPointSelection(
    target: HTMLElement,
    selectionHandlerFn: (selection: { point: any | any[]; isSelected: boolean }[]) => void,
    options: ChartPointSelectionMatchmakerOptions
  ) {
    this.selectionCounter = 1;

    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    if (chart.options.chart.type === 'gauge') {
      this.allowPointSelectionForGauge(chart, selectionHandlerFn);
      return;
    }

    this.checkSeriesToDisable(options, chart);

    this.originalClickHandlerFn = chart.options.plotOptions.series.point.events.click;

    if (chart.options.chart.type === 'dependencywheel') {
      this.allowPointSelectionForDependencyWheel(chart, selectionHandlerFn, options);
      return;
    }

    const disableSeries = this.disableSeries;
    const createMarker = this.createMarkerFn(chart);
    const alertService = this.alertService;

    const groupForPoint = (point) => {
      const seriesType = point.series.options.type ?? point.series.initialType;

      if (point.series.chart.xAxis[0].type === 'datetime') {
        return {
          group: point.category,
          seriesType,
        };
      }
      return {
        group: point.pointId || point.category,
        seriesType,
      };
    };
    const getDocumentCountForPoint = function (point) {
      const currentGroup = groupForPoint(point);

      switch (options.pointSelectionRule.rule) {
        case 'ALL_SAME_SERIES':
          return point.series.data.reduce((acc, p) => acc + (p.documentCount ?? p.y), 0);
        case 'ALL_SAME_GROUP':
          return point.series.chart.series
            .filter(
              (s) => s.visible && currentGroup.seriesType === (s.options.type ?? s.initialType)
            )
            .flatMap((s) => s.data.find((p) => currentGroup.group === groupForPoint(p).group) ?? [])
            .reduce((acc, p) => acc + (p.documentCount ?? p.y), 0);
        default:
          return point.documentCount ?? point.y;
      }
    };
    const hasRequiredDocumentCountForPoint = function (point) {
      const documentCount = getDocumentCountForPoint(point);

      return !!documentCount && documentCount >= Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT;
    };
    const isPointMatchingSelectionRule = function (point) {
      const uniqueSelectedPointsPerGroup = new Map<string, string>(
        point.series.chart.getSelectedPoints().map((p) => [groupForPoint(p).group, p.series.index])
      );
      const uniqueSelectedPointsPerSeries = new Map<string, string>(
        point.series.chart.getSelectedPoints().map((p) => [p.id, p.series.index])
      );
      const currentGroup = groupForPoint(point);
      let selectedPointCount = Math.max(
        uniqueSelectedPointsPerGroup.size,
        uniqueSelectedPointsPerSeries.size
      );

      switch (options.pointSelectionRule.rule) {
        case 'SAME_SERIES':
          selectedPointCount = uniqueSelectedPointsPerSeries.size;
          const seriesIdx = uniqueSelectedPointsPerSeries.values().next().value;
          if (seriesIdx && point.series.index !== seriesIdx) {
            return false;
          }
          break;
        case 'SAME_GROUP':
          selectedPointCount = uniqueSelectedPointsPerGroup.size;
          const selectedGroup = uniqueSelectedPointsPerGroup.keys().next().value;
          if (selectedGroup && selectedGroup !== currentGroup) {
            return false;
          }
          break;
        case 'ALL_SAME_SERIES':
          selectedPointCount = uniqueSelectedPointsPerSeries.size;
          break;
        case 'ALL_SAME_GROUP':
          selectedPointCount = uniqueSelectedPointsPerGroup.size;
          break;
      }

      const hasRequiredDocumentCount = hasRequiredDocumentCountForPoint(point);

      return hasRequiredDocumentCount && !point.selected && selectedPointCount < options.maxPoints;
    };

    const isPointMatchingSelectionScoring = function (point) {
      if (!options.pointSelectionRule.scoring || options.pointSelectionRule.scoring[0] === 'N/A') {
        return true;
      }

      const scoring = point.series.options.custom?.scoring?.toLocaleUpperCase();
      if (!scoring) {
        return true;
      }

      return options.pointSelectionRule.scoring.includes(scoring);
    };

    chart.update({
      plotOptions: {
        series: {
          point: {
            events: {
              click() {
                const hasRequiredDocumentCount = hasRequiredDocumentCountForPoint(this);
                const matchesSelectionRule = isPointMatchingSelectionRule(this);
                const matchesSelectionScoring = isPointMatchingSelectionScoring(this);

                if (matchesSelectionRule && matchesSelectionScoring) {
                  this.select(true, true);

                  if (options.pointSelectionRule.rule === 'SAME_SERIES') {
                    disableSeries(chart.series.filter((s) => s.index !== this.series.index));
                    chart.redraw();
                  }

                  if (options.pointSelectionRule.rule === 'ALL_SAME_GROUP') {
                    const currentGroup = groupForPoint(this);
                    const otherPointsForGroup = this.series.chart.series
                      .filter(
                        (s) =>
                          s.visible && currentGroup.seriesType === (s.options.type ?? s.initialType)
                      )
                      .flatMap(
                        (s) =>
                          s.data.find(
                            (p) => currentGroup.group === groupForPoint(p).group && p.id !== this.id
                          ) ?? []
                      );

                    for (const point of otherPointsForGroup) {
                      point.select(true, true);
                    }
                  }
                } else if (!hasRequiredDocumentCount) {
                  alertService.showToast(
                    translate('widgets.ai-insights.doc-threshold.below-min-snackbar', {
                      count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
                    })
                  );
                }
              },
              select() {
                const currentPoint = this;

                const makeSelection = function (point) {
                  createMarker(currentPoint);
                  selectionHandlerFn([
                    {
                      point,
                      isSelected: true,
                    },
                  ]);
                };

                if (options.pointSelectionRule.rule === 'ALL_SAME_GROUP') {
                  const currentGroup = groupForPoint(this);
                  const isPointForGroupSelected = this.series.chart.series
                    .filter((s) => currentGroup.seriesType === (s.options.type ?? s.initialType))
                    .some((s) =>
                      s.data.some(
                        (p) => p.selected && currentGroup.group === groupForPoint(p).group
                      )
                    );

                  if (isPointForGroupSelected) {
                    return;
                  }

                  const pointsInGroup = this.series.chart.series
                    .filter(
                      (s) =>
                        s.visible &&
                        currentGroup.seriesType === (s.options.type ?? s.initialType) &&
                        s.options.custom?.insightSelectable !== false
                    )
                    .flatMap((s) =>
                      s.data.filter(
                        (p) =>
                          currentGroup.group === groupForPoint(p).group &&
                          (p.documentCount ?? p.y) >=
                            Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT
                      )
                    );

                  if (chart.options.chart.type === 'pie') {
                    pointsInGroup.push({
                      x: currentPoint.x,
                      y: currentPoint.options.custom.comparisonY,
                      id: currentPoint.id,
                      pointId: currentPoint.pointId,
                      series: {
                        ...currentPoint.series,
                        index: 1,
                        options: {
                          custom: {
                            documentCount:
                              currentPoint.series.options.custom.comparisonDocumentCount,
                          },
                        },
                      },
                      options: {
                        custom: currentPoint.options.custom,
                      },
                    });
                  }

                  makeSelection(pointsInGroup);
                  return;
                }

                makeSelection(this);
              },
            },
          },
        },
      },
    });
  }

  private allowPointSelectionForGauge(chart, selectionHandlerFn) {
    const createMarker = this.createMarkerFn(chart);

    const makePointSelectable = (seriesIndex) => {
      const datapoint = chart.series[seriesIndex].data[0];

      datapoint.dataLabel.element.firstChild.classList.add(
        'highcharts-gauge-datapoint--selectable'
      );
      datapoint.dataLabel.on('click', function () {
        if (!datapoint.selected) {
          datapoint.select(true, true);
          createMarker(datapoint);

          selectionHandlerFn([
            {
              point: {
                y: datapoint.y,
                documentCount: datapoint.documentCount,
                series: {
                  index: datapoint.series.index,
                  name: datapoint.series.options.custom?.comparisonNames?.[seriesIndex],
                },
                custom: {},
              },
              isSelected: true,
            },
          ]);
        }
      });
    };

    makePointSelectable(0);

    if (chart.comparisonMetric) {
      makePointSelectable(1);
    }
  }

  private allowPointSelectionForDependencyWheel(
    chart,
    selectionHandlerFn,
    options: ChartPointSelectionMatchmakerOptions
  ) {
    const createMarker = this.createMarkerFn(chart);

    const alertService = this.alertService;
    let lastClickX;
    let lastClickY;

    chart.update({
      plotOptions: {
        series: {
          point: {
            events: {
              click(e) {
                if (this.selected || chart.getSelectedPoints().length >= options.maxPoints) {
                  return;
                }

                const selfNode = this.linksFrom?.[0] ?? this.linksTo?.[0] ?? this;
                const selfWeight = selfNode.custom?.selfWeight;

                const documentCount = this.weight ?? selfWeight;
                if (documentCount != null) {
                  const hasRequiredDocumentCount =
                    documentCount > Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT;

                  if (!hasRequiredDocumentCount) {
                    alertService.showToast(
                      translate('widgets.ai-insights.doc-threshold.below-min-snackbar', {
                        count: Constants.SMART_SUMMARY_MIN_DOC_COUNT_FOR_POINT,
                      })
                    );
                    return;
                  }
                }

                if (!this.weight || this.options.from === this.options.to) {
                  alertService.showToast(
                    translate('widgets.ai-insights.doc-threshold.no-colocation-crosstab')
                  );
                  return;
                }

                lastClickX = e.chartX;
                lastClickY = e.chartY;

                this.select(true, true);
              },
              select() {
                const { ...markerPoint } = { ...this, x: lastClickX, y: lastClickY };
                createMarker(markerPoint);
                selectionHandlerFn([
                  {
                    point: this,
                    isSelected: true,
                  },
                ]);
              },
            },
          },
        },
      },
    });
  }

  private checkSeriesToDisable(options: ChartPointSelectionMatchmakerOptions, chart) {
    if (options.pointSelectionRule.scoring && options.pointSelectionRule.scoring[0] !== 'N/A') {
      const nonMatchingScoreSeries = chart.series
        .filter((series) => series.options.custom?.scoring)
        .filter(
          (series) =>
            !options.pointSelectionRule.scoring.includes(
              series.options.custom.scoring.toLocaleUpperCase()
            )
        );
      this.disableSeries(nonMatchingScoreSeries);
    }

    this.disableSeries(chart.series.filter((s) => s.options.custom?.insightSelectable === false));
  }

  private disableSeries(series) {
    series.forEach((s) => {
      s.update(
        {
          opacity: 0.3,
          enableMouseTracking: false,
          states: { hover: { enabled: false }, inactive: { enabled: false } },
        },
        false
      );
    });
  }

  private createMarkerFn(chart) {
    const self = this;
    return function (point) {
      const marker = {
        id: `insight-point-${point.id}-${self.selectionCounter}`,
        draggable: '',
        labelOptions: {
          useHTML: true,
          distance: 1,
          shape: 'rect',
          backgroundColor: 'white',
        },
        labels: [
          {
            point: point.id ?? { x: point.x, y: point.y },
            text: `${INSIGHT_POINT_LABEL_HTML}${self.selectionCounter++}`,
          },
        ],
        visible: true,
      };

      return chart.addAnnotation(marker);
    };
  }

  disablePointSelection(target: HTMLElement) {
    const chart = this.chartDisplayService.getChart(target);
    if (!chart) {
      return;
    }

    if (chart.options.chart.type === 'gauge') {
      this.disablePointSelectionForGauge(chart);
    }

    if (this.originalClickHandlerFn) {
      chart.update(
        {
          plotOptions: {
            series: {
              point: {
                events: {
                  click: this.originalClickHandlerFn,
                },
              },
            },
          },
        },
        false
      );
    }

    this.enableSeries(chart.series);

    chart.redraw();

    for (const point of chart.getSelectedPoints()) {
      point.select(false);
    }

    const insightMarkers = chart.annotations.filter((a) =>
      a.options.id.match(/^insight-point-.+-\d+$/)
    );
    for (const marker of insightMarkers) {
      chart.removeAnnotation(marker.options.id);
    }
  }

  private disablePointSelectionForGauge(chart) {
    const datapoint = chart.series[0].data[0];
    datapoint.dataLabel.on('click');
    datapoint.dataLabel.element.firstChild.classList.remove(
      'highcharts-gauge-datapoint--selectable'
    );
  }

  private enableSeries(series) {
    series.forEach((s) => {
      s.update(
        {
          opacity: 1,
          enableMouseTracking: true,
          states: { hover: { enabled: true }, inactive: { enabled: true } },
        },
        false
      );
    });
  }
}
